// ABOUTME: Context bridging the entity detail page and the per-entity smart form wrappers.
// ABOUTME: Carries scope inputs + catalog options DOWN and dirty/valid/saving/action reporters UP.
import React, {createContext, useContext, useEffect} from 'react'

import type {LeadTimeFormOptions} from '../components/OrderIntakeLeadTimeForm'
import {
  LoadSpacingEntityOptions,
  OverbookingAllowanceEntityOptions,
  PumpSettingsEntityOptions,
  QuantityEntityOptions,
  RetarderEntityOptions,
  TrucksEntityOptions,
  UnloadingMethodEntityOptions,
} from '../types'
import type {ScopeLevel} from '../types'

/** Passed to onSuccess callbacks so callers can react to the type of operation that completed. */
export interface EntityActionResult {
  wasDelete?: boolean
  wasOverriding?: boolean
  createdScopeId?: string
}

/** Raw save/reset actions exposed by each `use<X>Entity` hook (success/error callbacks). */
export interface EntityFormActions {
  handleSave: (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => void
  handleReset: (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => void
}

export interface PendingScopeRequest {
  countryId: string
  businessLine: string
  level: ScopeLevel
  parentId: string
  identifiers: Record<string, string>
}

export interface EntityFormContextValue {
  // --- Down: scope inputs + page-level flags ---
  countryId: string
  businessLine: string
  selectedScopeId: string | undefined
  pendingScope: PendingScopeRequest | undefined
  rootScopeId: string | undefined
  isCountryDefault: boolean
  isReadOnly: boolean
  isSlotManagementEnabled: boolean
  /** Bumped on discard/leave/save to reset the rendered form's react-hook-form state. */
  formResetKey: number
  // --- Down: per-entity catalog options derived by the page ---
  quantityOptions: QuantityEntityOptions | undefined
  retarderOptions: RetarderEntityOptions | undefined
  unloadingOptions: UnloadingMethodEntityOptions | undefined
  leadTimeFormOptions: LeadTimeFormOptions | undefined
  leadTimeDistanceUomOptions: {value: string; label: string}[] | undefined
  dayCodes: string[] | undefined
  overbookingAllowanceOptions: OverbookingAllowanceEntityOptions | undefined
  pumpSettingsOptions: PumpSettingsEntityOptions | undefined
  loadSpacingOptions: LoadSpacingEntityOptions | undefined
  trucksOptions?: TrucksEntityOptions
  // --- Up: reporters the wrappers call ---
  onDirtyChange: (dirty: boolean) => void
  onValidChange: (valid: boolean) => void
  onSavingChange: (isSaving: boolean) => void
  registerActions: (actions: EntityFormActions) => void
}

const EntityFormContext = createContext<EntityFormContextValue | undefined>(undefined)

interface EntityFormProviderProps {
  value: EntityFormContextValue
  children: React.ReactNode
}

export const EntityFormProvider: React.FC<EntityFormProviderProps> = ({value, children}) => (
  <EntityFormContext.Provider value={value}>{children}</EntityFormContext.Provider>
)

export const useEntityFormContext = (): EntityFormContextValue => {
  const ctx = useContext(EntityFormContext)
  if (!ctx) {
    throw new Error('useEntityFormContext must be used within an EntityFormProvider')
  }
  return ctx
}

/**
 * Registers a wrapper's save/reset actions and reports its saving state up to the page.
 * Effects use stable deps (the hook's memoized actions object + the context's stable callbacks)
 * so registration fires once per mount and saving only re-reports when it flips.
 */
export const useRegisterEntityForm = (actions: EntityFormActions, isSaving: boolean): void => {
  const {registerActions, onSavingChange} = useEntityFormContext()

  useEffect(() => {
    registerActions(actions)
  }, [registerActions, actions])

  useEffect(() => {
    onSavingChange(isSaving)
    // Clear the reported saving state on unmount so a form leaving the panel (e.g. switching to an
    // entity type with no registered form) can't leave the footer spinner stuck on.
    return () => onSavingChange(false)
  }, [onSavingChange, isSaving])
}
